import type { CommentPlan } from '@constants'
import type { PdfTargetViewer, PlanTemplateTypeDTO } from '@api/fastAPI.schemas'
import type { PlanElementType } from '@editorStores'

/** The two plan categories the plan view can preview (the load plan has no scene preview). */
export type PlanViewPlanType = Extract<CommentPlan, 'positionPlan' | 'stiffeningPlan'>

export const PLAN_VIEW_PLAN_TYPES: readonly PlanViewPlanType[] = ['positionPlan', 'stiffeningPlan']

export const DEFAULT_PLAN_VIEW_PLAN_TYPE: PlanViewPlanType = 'positionPlan'

/** Plantyp choices: the previewable plans plus the load plan, which can only be generated. */
export type PlanViewPlanChoice = PlanViewPlanType | 'loadPlan'

export const PLAN_VIEW_PLAN_CHOICES: readonly PlanViewPlanChoice[] = [
  ...PLAN_VIEW_PLAN_TYPES,
  'loadPlan',
]

/**
 * Choices of the plan setting "Optimiert für": the PDF viewer generated plans encode their
 * annotations for. PDF viewers read annotations differently.
 */
export const PLAN_TARGET_VIEWER_OPTIONS: readonly { value: PdfTargetViewer; label: string }[] = [
  { value: 'bluebeam', label: 'Bluebeam' },
  { value: 'pdf-xchange', label: 'PDF-XChange' },
]

/** Default of the plan setting "Optimiert für", as in the backend. */
export const DEFAULT_PLAN_TARGET_VIEWER: PdfTargetViewer = 'bluebeam'

/** How users know the viewer (its label in the plan setting "Optimiert für"). */
export const getTargetViewerLabel = (targetViewer: PdfTargetViewer): string =>
  PLAN_TARGET_VIEWER_OPTIONS.find(option => option.value === targetViewer)?.label ?? targetViewer

/** Element types drawn in the scene for each plan — everything else is hidden. */
export const PLAN_VIEW_VISIBLE_ELEMENT_TYPES: Record<PlanViewPlanType, readonly ElementTypes[]> = {
  positionPlan: [
    'vertical_slabs',
    'vertical_roof_slabs',
    'columns',
    'beams',
    'purlins',
    'rips',
    'lintels',
    'inner_walls',
    'outer_walls',
  ],
  stiffeningPlan: ['inner_walls', 'outer_walls'],
}

/**
 * Element types drawn when the roof is chosen as the plan's storey. The roof is no floorplan
 * storey of its own — its elements are drawn on the top storey's floorplan — so "Dach" shows the
 * top storey with everything but these hidden. Ceiling positions, beams, columns, rips, lintels
 * and walls can appear here when their plan storeys include the roof.
 */
export const PLAN_VIEW_ROOF_ELEMENT_TYPES: readonly ElementTypes[] = [
  'vertical_slabs',
  'vertical_roof_slabs',
  'purlins',
  'beams',
  'columns',
  'rips',
  'lintels',
  'inner_walls',
  'outer_walls',
]

/** Element types whose labels are shown for each plan (the position plan shows all but walls). */
export const PLAN_VIEW_LABEL_ELEMENT_TYPES: Record<PlanViewPlanType, readonly ElementTypes[]> = {
  positionPlan: [
    'vertical_slabs',
    'vertical_roof_slabs',
    'beams',
    'columns',
    'purlins',
    'rips',
    'lintels',
  ],
  stiffeningPlan: ['outer_walls', 'inner_walls'],
}

/** Plan template (`export/pdf_templates/<type>/`) the export lays each plan on. */
export const PLAN_VIEW_TEMPLATE_TYPES: Record<PlanViewPlanType, PlanTemplateTypeDTO> = {
  positionPlan: 'position',
  stiffeningPlan: 'stiffening',
}

/** Whether the labels carry the PDF's second cross-section line ("inkl. Querschnitte"). */
export const PLAN_VIEW_SHOW_LABEL_CROSS_SECTIONS: Record<PlanViewPlanType, boolean> = {
  positionPlan: true,
  stiffeningPlan: false,
}

/**
 * Position groupings whose `exported` flag decides what the position plan export draws. Loaded to
 * hide the non-exported positions in the position plan preview.
 */
export const EXPORT_FILTER_POSITION_GROUPING_TYPES: readonly PositionGroupingType[] = [
  'beams',
  'columns',
  'purlins',
  'slab-beams',
  'roof-slab-beams',
  'wall-rips',
  'wall-lintels',
]

/** Plans store their name as the tree item's `node_name` (VARCHAR(255)); the backend rejects more. */
export const PLAN_NAME_MAX_LENGTH = 255

/** Block action held while the "Pläne" tab's preview has unsaved PDF changes. */
export const PLAN_VIEW_PLAN_PDF_BLOCK_ACTION = 'plan-view-plan-pdf'

export const PLAN_VIEW_PLAN_PDF_CHANGED_MESSAGE =
  'PDF Plan wurde angepasst, bitte speichern oder verwerfen'

/** The plan element lists of the plan view's list column (ids of the element list tabs). */
export type PlanViewPlanElementListId = 'tables' | 'comments' | 'image-cards'

/** The list each listed plan element is in (element labels are not listed). */
export const PLAN_VIEW_PLAN_ELEMENT_LIST_BY_TYPE: Partial<
  Record<PlanElementType, PlanViewPlanElementListId>
> = {
  table: 'tables',
  comment: 'comments',
  'image-card': 'image-cards',
}
